import { Component, OnInit, OnDestroy, ViewChild, DoCheck } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';

import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { PaymentsService } from '@app/services/net-remit/payments.service';
import { SharedService } from '@app/services/net-remit/shared-functions.service';

import { ConfirmationService, MessageService as PrimeMessageService } from 'primeng/api';

import { MemoPaymentSimpleView } from '@app/shared/models/MemoPaymentSimpleView';
import { MemoPaymentInfoView } from '@app/shared/models/MemoPaymentInfoView';
import { MemoPaymentView } from '@app/shared/views/MemoPaymentView';
import { RelatedDocsView } from '@app/shared/views/RelatedDocsView';
import { ExcludedDocumentView } from '@app/shared/views/ExcludedDocumentView';
import { MemoDocsView } from '@app/shared/views/MemoDocsView';
import { MemoRelatedDocsView, MemoView } from '@app/shared/views/MemoRelatedDocsView';
import { MemoStatusView } from '@app/shared/views/MemoStatusView';
import { routesConstants } from '@app/shared/models/LinksView';

import { finalize, takeWhile } from 'rxjs/operators';
import { fadeIn } from 'assets/animations/animations';
import { default as constantsFile } from '@app/shared/constants.json';
import {MemoPaymentWrapperView} from '@app/shared/models/MemoPaymentWrapperView';

@Component({
    selector: 'app-payment-management-detail',
    templateUrl: './payment-management-detail.component.html',
    styleUrls: ['./payment-management-detail.component.scss'],
    animations: [fadeIn],
    standalone: false
})

export class PaymentManagementDetailComponent implements OnInit, OnDestroy, DoCheck {

  alive: boolean = true;
  constants: any = constantsFile.constants;
  entryFileDisable: boolean;
  hasError: boolean;
  isAdmin: boolean;
  isDownloadError: boolean;
  isLoading: boolean = true;
  rejectFileEnable: boolean;
  reprocessableEnable: boolean;
  sendBspLinkEnable: boolean;
  cancelEnable: boolean;

  downloadedFileName: string;
  paymentDetailTitle: string;

  customerId: number;
  paymentId: number;

  downloadedBlob: Blob;

  memoPayment: MemoPaymentView;

  // memos list
  showMemosList: boolean;
  showExcludeDocuments: boolean = false;
  isExcludeReadOnly: boolean = false;
  isLoadingMemoList: boolean = true;
  isLoadingRelatedList: boolean = true;
  index: number = 0;
  memoDocs: Array<MemoDocsView> = new Array<MemoDocsView>();
  relatedDocs: Array<RelatedDocsView> = new Array<RelatedDocsView>();
  relatedDocsToShow: Array<RelatedDocsView> = new Array<RelatedDocsView>();
  amountGrouped: string;
  amountGroupedByCurrency: string;
  amountGroupedCurrency: string;
  searchValueMemoDoc: string;
  searchValueRelatedDoc: string;
  selectAll: boolean = false;

  callOnlyOnceMemDoc: boolean;
  callOnlyOnceRelDoc: boolean;

  @ViewChild('memDoc') private tableMemDoc: Table;
  @ViewChild('relDoc') private tableRelDoc: Table;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private paymentsService: PaymentsService,
    private primeMessageService: PrimeMessageService,
    private translateService: TranslateService,
    private router: Router,
    private sharedService: SharedService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.paymentId = this.activatedRoute.snapshot.params['paymentId'];
    this.customerId = this.activatedRoute.snapshot.params['customerId'];
    this.isAdmin = this.authService.credentials.roleName.includes(this.constants.roleNames.admin);
    this.searchValueMemoDoc    = localStorage.getItem("memoDocFilter") ? localStorage.getItem("memoDocFilter") : "";

    this.getPaymentDetailData();
  }

  ngDoCheck() {
    if (this.tableMemDoc != undefined && !this.callOnlyOnceMemDoc && this.showMemosList) {
      this.callOnlyOnceMemDoc = true;
      this.filterTableMemDoc(this.searchValueMemoDoc);
    }
    if (this.tableRelDoc != undefined && !this.callOnlyOnceRelDoc && this.showMemosList) {
      this.callOnlyOnceRelDoc = true;
      this.isLoadingRelatedList = false;
    }
  }

  public filterTableMemDoc(searchValue: string) {
    this.tableMemDoc.filterGlobal(searchValue, 'contains');
    setTimeout(() => {
      this.isLoadingMemoList = false;
    }, 300);
  }

  public filterTableRelDoc(searchValue: string) {
    this.tableRelDoc.filterGlobal(searchValue, 'contains');
    setTimeout(() => {
      this.isLoadingRelatedList = false;
    }, 300);
  }

  public getPaymentDetailData(){
    this.isLoading = true;
    this.hasError = false;
    this.paymentsService.findPaymentDetail(this.paymentId, this.isAdmin ? this.customerId : null)
      .pipe(finalize(() => this.isLoading = false ), takeWhile(() => this.alive))
      .subscribe({ next: (data) => {
        this.hasError = false;
        this.setMemoPayment(data);
      }, error: () => {
        this.hasError = true;
        this.primeMessageService.add({
          closable: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('Error Generic Onload')
        });
      }});
  }

  private setMemoPayment(data: MemoPaymentView) {
    this.memoPayment = data;

    const emptyPayment: string = "---";
    this.memoPayment.fromDocument = this.memoPayment.fromDocument === "-1" ? emptyPayment : this.memoPayment.fromDocument.toString().padStart(10, "0");
    this.memoPayment.toDocument = this.memoPayment.toDocument === "-1" ? emptyPayment : this.memoPayment.toDocument.toString().padStart(10, "0");
    this.memoPayment.fromDocumentAdm = this.memoPayment.fromDocumentAdm === "-1" ? emptyPayment : this.memoPayment.fromDocumentAdm.toString().padStart(10, "0");
    this.memoPayment.toDocumentAdm = this.memoPayment.toDocumentAdm === "-1" ? emptyPayment : this.memoPayment.toDocumentAdm.toString().padStart(10, "0");

    this.entryFileDisable = this.paymentsService.isEntryFileDisabled(this.memoPayment.memoStatus.memoStatusId);

    // Se visualiza el botón de Enviar si se cumplen las siguientes condiciones:
    // - Si el estado es alguno de estos: unexpected-error, unable-to-connect, connection-timed-out, connection-rejected, pendingAudit.
    // - Si tiene rol auditor o administrador.
    this.sendBspLinkEnable  = this.paymentsService.canSendToBspLink(this.authService.credentials.roleName, this.memoPayment.memoStatus.memoStatusId);
    this.cancelEnable = this.memoPayment.memoStatus.status == this.constants.memoStatus.prepared
    || this.memoPayment.memoStatus.status == this.constants.memoStatus.pendingAudit;

    this.rejectFileEnable = this.memoPayment.memoStatus.status == this.constants.memoStatus.prepared
    || this.memoPayment.memoStatus.status == this.constants.memoStatus.pending
    || this.memoPayment.memoStatus.status == this.constants.memoStatus.errorCreatingFile;

    this.reprocessableEnable = this.memoPayment.memoStatus.status == this.constants.memoStatus.errorExtractingData;

    this.memoPayment.memoStatus.status     = this.translateService.instant('MemoPayments.' + this.memoPayment.memoStatus.sStatusComplete);
    this.paymentDetailTitle = this.translateService.instant('MemoPayments.Payment Detail Of') + ' ' + this.memoPayment.fileName;

    if (this.memoPayment.oMemosList !== null) {
      this.getMemoDocs(this.memoPayment.oMemosList);
    } else {
      this.isLoadingMemoList = false;
      this.isLoadingRelatedList = false;
    }
  }

  private getMemoDocs(memosList: MemoRelatedDocsView) {
    let memoDocsTmp: Array<MemoDocsView> = new Array<MemoDocsView>();
    let relatedDocsTmp: Array<RelatedDocsView> = new Array<RelatedDocsView>();
    let amountGroupedByCurrencyMap: Map<string, string> = new Map<string, string>();
    memosList.content.agent.forEach(a => {
      let memos: Array<MemoView> = a.memo;
      memos.map(a => {
        let sumValue = parseFloat(amountGroupedByCurrencyMap.get(a.currencyObject.currency));
        let totalValue = sumValue? sumValue + a.incentivesAmount : a.incentivesAmount;
        amountGroupedByCurrencyMap.set(a.currencyObject.currency, totalValue.toFixed(memos[0].currencyObject.decimals));
      });
      memos.forEach((m: MemoView) => {
        const valueSplitted = m.incentivesAmount.toString().split(".");
        if(valueSplitted.length === 1) valueSplitted.push("0");
        let formattedValueTmp = m.currencyObject.decimals > 0 ? valueSplitted[0] + "." + valueSplitted[1].padEnd(m.currencyObject.decimals, "0") : valueSplitted[0];
        let formattedValue: number = +formattedValueTmp;
        memoDocsTmp.push(new MemoDocsView(m.docNumber, a.code, formattedValue, memosList.vat + "%", m.currencyObject.currency + m.currencyObject.decimals));

        if (m.relatedList) {
          m.relatedList.flatMap(rl => rl?.related || []).forEach(r => {
            const formattedValue = +r.incentiveAmount.toFixed(memos[0].currencyObject.decimals);
            relatedDocsTmp.push(new RelatedDocsView(m.docNumber, m.memoType, r.document, m.issueRefund, r.issuer, formattedValue, m.currencyObject.currency + m.currencyObject.decimals, false));
          });
        }
      });
    });

    for (let key of amountGroupedByCurrencyMap.keys()) {
      this.amountGroupedByCurrency = ((this.amountGroupedByCurrency === undefined) ? '' : this.amountGroupedByCurrency + ', ' ) + amountGroupedByCurrencyMap.get(key) + " " + key;
    }

    //Pending refactor
    this.amountGrouped         = parseFloat(this.amountGroupedByCurrency.substring(0, this.amountGroupedByCurrency.indexOf(" "))).toFixed(2);
    this.amountGroupedCurrency = this.amountGroupedByCurrency.substring(this.amountGroupedByCurrency.indexOf(" "), this.amountGroupedByCurrency.length);

    this.memoDocs = memoDocsTmp;
    this.relatedDocs = relatedDocsTmp;
    this.isLoadingMemoList = false;
    this.isLoadingRelatedList = false;
  }

  protected toggleMemoList(showExcludeDocuments: boolean) {
    this.paymentsService.getExcluded(this.paymentId, this.customerId)
      .subscribe((excludedList: ExcludedDocumentView[]) => {
        this.relatedDocs.sort((a, b) => {
          if (a.document === b.document) {
            return a.relatedDoc - b.relatedDoc;
          }
          return a.document - b.document;
        });

        // Match the excluded flags by document identity (related document + issuer agent + currency)
        // instead of by array position. Positional matching broke for cancelled payments because the
        // backend's MemoDocument ordering key is overwritten when the replacement payment reuses the
        // memo numbers, which put the checkbox on the wrong related document.
        const excludedKeys = new Set(
          (excludedList || []).map(e => `${e.document}|${e.issuerAgent}|${(e.currency || '').trim()}`)
        );

        this.relatedDocs.forEach(doc => {
          const key = `${doc.relatedDoc}|${doc.agent}|${(doc.currency || '').trim()}`;
          doc.excluded = excludedKeys.has(key);
        });

        // Check if any documents are already excluded (payment was cancelled and recreated)
        const hasExcludedDocs = excludedKeys.size > 0;
        
        // If viewing excluded documents or if there are already excluded docs, show all
        // Otherwise filter out excluded ones
        this.relatedDocsToShow = (showExcludeDocuments || hasExcludedDocs) ? this.relatedDocs : this.relatedDocs.filter(doc => !doc.excluded);

        this.showMemosList = !this.showMemosList;
        if (this.showMemosList) {
          // Show exclude column if we're in exclude mode OR if there are already excluded docs
          this.showExcludeDocuments = showExcludeDocuments || hasExcludedDocs;
          // Make it read-only if there are already excluded docs and we're not in edit mode
          this.isExcludeReadOnly = hasExcludedDocs && !showExcludeDocuments;
          
          if (this.index === 0) {
            this.isLoadingMemoList = true;
            this.callOnlyOnceMemDoc = false;
          } else {
            this.isLoadingRelatedList = true;
            this.callOnlyOnceRelDoc = false;
          }
        }
      });
  }

  public reprocessPayment() {
    this.reprocessableEnable = false;
    let memoPaymentInfoView = new MemoPaymentInfoView(this.paymentId, this.memoPayment.customer.customerId);
    this.paymentsService.reprocessPayment(memoPaymentInfoView)
      .pipe(finalize(() => {this.isLoading = false; } ), takeWhile(() => this.alive))
      .subscribe({ next: (paymentSaved) => {
        this.hasError = false;
        this.primeMessageService.add({
          closable: true,
          severity:'success',
          summary: this.translateService.instant('Modal.Success'),
          detail: this.translateService.instant('MemoPayments.Payment Single Saved', { param: paymentSaved.fileName })
        });
      },error: () => {
        this.primeMessageService.add({
          closable: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('MemoPayments.Error Reprocessing Payment')
        });
      }});
  }

  public cancelPayment() {
    let memoPaymentInfoView = new MemoPaymentInfoView(this.paymentId, this.memoPayment.customer.customerId);
    this.paymentsService.cancelPayment(this.paymentId, memoPaymentInfoView)
      .pipe(finalize(() => { this.isLoading = false, this.cancelEnable = false, this.sendBspLinkEnable = false }), takeWhile(() => this.alive))
      .subscribe({
        next: () => {
          this.hasError = false;
          // Refresh payment detail
          this.memoPayment.fromDocument = "0";
          this.memoPayment.toDocument = "0";
          this.cancelEnable = false;
          this.rejectFileEnable = false;
          this.memoPayment.memoStatus = new MemoStatusView();
          this.memoPayment.memoStatus.memoStatusId = this.constants.memoStatusId.cancelled;
          this.memoPayment.memoStatus.status = this.constants.memoStatus.cancelled;
          // Toast success message
          this.primeMessageService.add({
            closable: true,
            severity: 'success',
            summary: this.translateService.instant('Modal.Success'),
            detail: this.translateService.instant('MemoPayments.cancelled')
          })
        }, error: () => {
          this.hasError = true;
          this.showMemosList = false;
          this.primeMessageService.add({
            closable: true,
            severity: 'error',
            summary: this.translateService.instant('Modal.Error'),
            detail: this.translateService.instant('Error Cancelling Payment')
          });
        }
      }
    );
  }

  public cancelPaymentAlert() {
    this.confirmationService.confirm({
      message: this.translateService.instant('Text Cancel Payment'),
      header: this.translateService.instant('Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('Yes'),
      rejectLabel: this.translateService.instant('No'),
      accept: () => {
        this.cancelPayment();
      }}
    );
  }

  public updateStatus(status: string) {
    this.isLoading = true;
    let memoPaymentSimple = new MemoPaymentSimpleView(status, this.isAdmin ? this.customerId : null);
    this.paymentsService.updateStatus(this.paymentId, memoPaymentSimple)
      .pipe(finalize(() => {
        this.isLoading = false;
        if (status === this.constants.memoStatus.cancelled) {
          this.rejectFileEnable = false;
          this.cancelEnable = false;
        }
      }), takeWhile(() => this.alive))
      .subscribe({
        next: () => {
          this.hasError = false;
          this.showMemosList = false;
          this.memoPayment.memoStatus.memoStatusId = this.constants.memoStatus[status];
          this.memoPayment.memoStatus.status = this.translateService.instant('MemoPayments.' + status + '-Complete');
          this.memoPayment.memoStatus.sStatusComplete = status + '-Complete';
          if (status === this.constants.memoStatus.sending) {
            this.sendBspLinkEnable = false;
            this.primeMessageService.add({
              closable: true,
              severity: 'success',
              summary: this.translateService.instant('Modal.Success'),
              detail: this.translateService.instant('MemoPayments.Sending BspLink')
            });
          }
        },
        error: () => {
          this.hasError = true;
          this.primeMessageService.add({
            closable: true,
            severity: 'error',
            summary: this.translateService.instant('Modal.Error'),
            detail: this.translateService.instant('Error Updating Status File')
          });
        }
      });
  }

  public getFileToDownload(typeFile: string) {
    this.paymentsService.downloadPaymentsFiles(typeFile, this.memoPayment.memoPaymentId, this.isAdmin ? this.customerId : null)
			.pipe(finalize(() => this.isLoading = false ), takeWhile(() => this.alive))
			.subscribe({ next: (data) => {
        this.isDownloadError = false;
        this.downloadedFileName = data.fileName;
        this.downloadedBlob = this.sharedService.getBlob(data.fileContent, data.mimeType);
        this.sharedService.downloadFile(this.downloadedBlob, this.downloadedFileName);
			}, error: () => {
        this.isDownloadError = true;
        this.primeMessageService.add({
          closable: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('Error File Download')
        });
      }});
  }

  public viewTaskDetail(memoPayment: MemoPaymentView) {
    this.router.navigate([routesConstants.paymentManagement.paymentManagement + '/' + routesConstants.paymentManagement.taskDetail + '/' + memoPayment.parentId + '/customer/' + this.customerId]);
  }

  public clearMemoDoc(table: Table) {
    this.searchValueMemoDoc = '';
    table.clear();
  }

  public clearRelatedDocsFilter(table: Table) {
    table.filterGlobal('', 'contains');
    table.clearFilterValues();
    this.updateSelectAllState();
  }

  public toggleAllCheckboxes(): void {
    // Get the filtered data from the PrimeNG table
    // If filters are applied, tableRelDoc.filteredValue contains only the filtered rows
    // If no filters, it's null, so we use the full relatedDocsToShow array
    const visibleDocs = this.tableRelDoc?.filteredValue || this.relatedDocsToShow;
    
    // Only toggle checkboxes for documents that are currently visible (filtered/displayed)
    visibleDocs.forEach(doc => doc.excluded = this.selectAll);
  }

  // Keeps the header "select all" checkbox in sync with the rows that are currently visible.
  // Without this, checking "select all" while a filter is active would leave the header checkbox
  // checked after the filter is removed, even though the newly revealed rows are not excluded.
  public updateSelectAllState(): void {
    const visibleDocs = this.tableRelDoc?.filteredValue || this.relatedDocsToShow;
    this.selectAll = visibleDocs.length > 0 && visibleDocs.every(doc => doc.excluded);
  }

  public cancelAllCheckboxes(): void {
    // Cancel only clears every selection; it must keep the "Remove" column and its
    // action buttons visible (they are gated on showExcludeDocuments), so we do not
    // toggle showExcludeDocuments here.
    this.selectAll = false;
    this.relatedDocs.forEach(doc => doc.excluded = false);
  }

  public saveExcludedDocuments(): void {
    if (this.relatedDocs.length === this.relatedDocs.filter(doc => doc.excluded).length) {
      this.primeMessageService.add({
        closable: true,
        severity: 'error',
        summary: this.translateService.instant('Modal.Error'),
        detail: this.translateService.instant('MemoPayments.Memo Documents Excluded All')
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translateService.instant('MemoPayments.Text Exclude Document'),
      header: this.translateService.instant('Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('Yes'),
      rejectLabel: this.translateService.instant('No'),
      accept: () => {
        this.paymentsService.excludeDocuments(this.paymentId, this.customerId, this.relatedDocs.filter(doc => doc.excluded))
          .pipe(takeWhile(() => this.alive))
          .subscribe({
            next: (paymentSaved) => {
              this.hasError = false;
              // Keep the view open but make the Remove column read-only
              this.isExcludeReadOnly = true;
              this.primeMessageService.add({
                closable: true,
                severity: 'success',
                summary: this.translateService.instant('Modal.Success'),
                detail: this.translateService.instant('MemoPayments.Memo Documents Excluded')
              });
            }, error: () => {
              this.primeMessageService.add({
                closable: true,
                severity: 'error',
                summary: this.translateService.instant('Modal.Error'),
                detail: this.translateService.instant('MemoPayments.Error Excluding Memo Documents')
              });
            }
          });
      },
      reject: () => {
      }
    })
  }

  protected goBack() {
    this.router.navigate([routesConstants.paymentManagement.paymentManagement]);
  }

  ngOnDestroy(): void {
		// prevent memory leak when component destroyed
    this.alive = false;
    if(!this.router.routerState.snapshot.url.includes(routesConstants.paymentManagement.paymentManagement + "/" + routesConstants.paymentManagement.detail)){
      this.searchValueMemoDoc = '';
      localStorage.setItem('memoDocFilter', "");
    } else {
      localStorage.setItem('memoDocFilter', this.searchValueMemoDoc);
    }
  }
}
